import { resolveCampaignStatusAndProgress } from "@/hooks/useCampaigns";
import type { ProgressDetails, TransformedInfluencer } from "../types";

export function truncateToWords(text: string, wordCount = 12): string {
  if (!text) return text;
  const words = text.split(" ");
  if (words.length <= wordCount) return text;
  return words.slice(0, wordCount).join(" ") + "...";
}

export function getProgressDetails(
  startDate?: string,
  endDate?: string,
  campaignStatus?: string
): ProgressDetails {
  const startStr = startDate;
  const endStr = endDate;

  if (!startStr || !endStr || startStr === "Not set" || endStr === "Not set") {
    return {
      progressPercent: 0,
      completedDays: 0,
      totalDays: 0,
      text: "No dates configured",
      badge: "Pending",
    };
  }

  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return {
      progressPercent: 0,
      completedDays: 0,
      totalDays: 0,
      text: "Invalid dates",
      badge: "Pending",
    };
  }

  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startZero = new Date(start);
  startZero.setHours(0, 0, 0, 0);
  const endZero = new Date(end);
  endZero.setHours(0, 0, 0, 0);

  let completedDays = 0;
  if (today < startZero) {
    completedDays = 0;
  } else if (today > endZero) {
    completedDays = totalDays;
  } else {
    completedDays = Math.ceil((today.getTime() - startZero.getTime()) / (1000 * 60 * 60 * 24));
  }

  const lifecycle = resolveCampaignStatusAndProgress(startStr, endStr, campaignStatus || "");
  const progressPercent = lifecycle.progress;
  const text = `${completedDays} of ${totalDays} days completed`;

  let badge: "On Track" | "Completed" | "Paused" | "Pending" = "On Track";
  if (lifecycle.status === "completed") {
    badge = "Completed";
  } else if (lifecycle.status === "paused") {
    badge = "Paused";
  }

  return { progressPercent, completedDays, totalDays, text, badge };
}

export function calculateCommittedFees(influencers: TransformedInfluencer[]): number {
  return influencers.reduce((acc, inf) => {
    const rawFee = inf.rawFee;
    if (typeof rawFee === "number") return acc + rawFee;
    const fee = typeof inf.totalPayment === "string"
      ? parseFloat(inf.totalPayment.replace(/[₹$,]/g, ""))
      : 0;
    return acc + (isNaN(fee) ? 0 : fee);
  }, 0);
}

export function formatDateLong(dateStr?: string): string {
  if (!dateStr) return "Not set";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
